import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { CrownSimple, Buildings, Users, Clock, TrendUp, ArrowsClockwise, Plus, X, CreditCard, Sparkle, Check, XCircle, CaretDown, CaretRight, EnvelopeSimple, ArrowsCounterClockwise } from "@phosphor-icons/react";
import api, { formatApiError, errContext } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Navigate } from "react-router-dom";
import RevenueWidget from "./platform/RevenueWidget";
import BulkUsersModal from "./platform/BulkUsersModal";
import DocumentationSection from "./platform/DocumentationSection";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

function TapModeBadge({ mode }) {
  if (!mode) return null;
  const map = {
    live: { label: "TAP LIVE", cls: "bg-emerald-600 text-white border-emerald-700", tip: "Real charges. Every checkout hits your live Tap merchant account." },
    test: { label: "TAP TEST", cls: "bg-amber-500 text-white border-amber-600", tip: "Sandbox key. Cards are simulated — no real money moves." },
    not_configured: { label: "TAP OFF", cls: "bg-zinc-600 text-white border-zinc-700", tip: "No TAP_SECRET_KEY in env. Checkout endpoints will 503." },
  };
  const m = map[mode] || map.not_configured;
  return (
    <span title={m.tip}
      data-testid={`tap-mode-${mode}`}
      className={`inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.15em] uppercase border px-2 py-1 ${m.cls}`}>
      <CreditCard size={10} weight="fill" /> {m.label}
    </span>
  );
}

function FawtaraModeBadge({ status }) {
  const [testing, setTesting] = useState(false);
  if (!status) return null;
  const isLive = status.mode === "live";
  const cls = isLive
    ? "bg-emerald-600 text-white border-emerald-700"
    : "bg-amber-500 text-white border-amber-600";
  const smokeTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/platform/fawtara-smoke-test");
      if (data.mode === "mock") {
        toast.info(`Fawtara mock ack · sample ${data.sample_id}`, {
          description: data.message,
          duration: 8000,
        });
      } else if (data.ok) {
        toast.success(`Fawtara OTA reachable · HTTP ${data.upstream_status}`, {
          description: `Sample ${data.sample_id} accepted. Body preview: ${data.upstream_body?.slice(0, 120) || "(empty)"}`,
          duration: 10000,
        });
      } else {
        toast.error(`Fawtara OTA test FAILED · HTTP ${data.upstream_status || "network"}`, {
          description: (data.upstream_body || data.error || "").slice(0, 220),
          duration: 12000,
        });
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Fawtara test failed");
    } finally {
      setTesting(false);
    }
  };
  return (
    <div className="inline-flex items-center gap-1">
      <span
        title={status.hint || ""}
        data-testid={`fawtara-mode-${status.mode}`}
        className={`inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.15em] uppercase border px-2 py-1 ${cls}`}
      >
        <Sparkle size={10} weight="fill" /> FAWTARA {isLive ? "LIVE" : "MOCK"}
      </span>
      <button
        onClick={smokeTest}
        disabled={testing}
        data-testid="fawtara-smoke-test-btn"
        title={isLive ? "Fire a synthetic invoice at the live OTA endpoint." : "Verify the mock stub (no credentials configured)."}
        className="font-mono text-[10px] tracking-[0.12em] uppercase border border-zinc-700 px-2 py-1 hover:bg-zinc-950 hover:text-white disabled:opacity-40"
      >
        {testing ? "TESTING…" : "TEST"}
      </button>
    </div>
  );
}

function ResetDemoButton({ onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const reset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/platform/demo/reset");
      const total = data.wiped_total || 0;
      toast.success(`Demo reset · wiped ${total} rows and reseeded`);
      setOpen(false);
      onDone && onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="reset-demo-btn"
        title="Wipe every document in tenant-demo and rerun the seed script — restores demo@ and demo-user@ to a pristine state."
        className="font-mono text-[10px] tracking-[0.15em] uppercase border border-amber-400 text-amber-400 px-3 py-1.5 hover:bg-amber-400 hover:text-zinc-950 transition-colors inline-flex items-center gap-2"
      >
        <ArrowsCounterClockwise size={12} /> RESET DEMO
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="reset-demo-modal">
          <div className="bg-white border-2 border-zinc-950 max-w-md w-full">
            <div className="border-b border-zinc-950 bg-amber-400 px-5 py-3">
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-950">DESTRUCTIVE ACTION</div>
              <h3 className="text-xl font-black tracking-tight text-zinc-950 mt-1">Reset demo tenant?</h3>
            </div>
            <div className="p-5 text-sm text-zinc-700 space-y-3">
              <p>
                This will delete <strong>every leave, invoice, employee, notification, holiday, and workflow</strong> in the <code className="bg-zinc-100 px-1">tenant-demo</code> workspace, then re-run the seed script to restore the default sample data.
              </p>
              <p>
                <strong>Keeps:</strong> the two demo user accounts (<code className="bg-zinc-100 px-1">demo@…</code> + <code className="bg-zinc-100 px-1">demo-user@…</code>) with their documented passwords.
              </p>
              <p className="text-xs font-mono text-[#D0021B]">This cannot be undone.</p>
            </div>
            <div className="border-t border-zinc-200 px-5 py-3 flex justify-end gap-2">
              <button onClick={() => setOpen(false)} disabled={busy} className="text-xs font-mono uppercase text-zinc-500 hover:text-zinc-950 px-3 py-1.5" data-testid="reset-demo-cancel">
                Cancel
              </button>
              <button
                onClick={reset}
                disabled={busy}
                className="text-xs font-mono uppercase text-white bg-[#D0021B] hover:bg-red-800 px-4 py-1.5 disabled:opacity-40 flex items-center gap-1"
                data-testid="reset-demo-confirm"
              >
                {busy ? "RESETTING…" : (<><ArrowsCounterClockwise size={11} /> Yes, wipe &amp; reseed</>)}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function EnterpriseTemplateModal({ open, onClose, onCreated }) {
  const [orgName, setOrgName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [seats, setSeats] = useState(100);
  const [trialDays, setTrialDays] = useState(30);
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const submit = async () => {
    if (!orgName.trim() || !ownerEmail.trim()) {
      toast.error("Organization name and owner email are required.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/platform/tenants/enterprise-template", {
        org_name: orgName.trim(),
        owner_email: ownerEmail.trim(),
        seats: Math.max(1, seats),
        trial_days: Math.max(0, trialDays),
      });
      // Surface the invite link so the operator can hand-deliver it if email fails
      toast.success(
        `Provisioned "${data.org_name}" · ${data.seats_purchased} seats. Copy activation link?`,
        {
          action: {
            label: "COPY LINK",
            onClick: () => {
              navigator.clipboard?.writeText(data.invite_link);
              toast.success("Activation link copied");
            },
          },
          duration: 10000,
        }
      );
      setOrgName(""); setOwnerEmail(""); setSeats(100); setTrialDays(30);
      onCreated?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, errContext(err)));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/70 flex items-center justify-center p-4" data-testid="enterprise-modal">
      <div className="w-full max-w-lg bg-white border-2 border-zinc-950">
        <div className="bg-zinc-950 text-white px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CrownSimple size={14} weight="fill" className="text-[#FF3E00]" />
            <span className="font-mono text-[11px] tracking-[0.2em]">/ NEW ENTERPRISE TENANT</span>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="enterprise-modal-close"><X size={14} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <div className="font-mono text-[10px] tracking-[0.18em] text-zinc-500 uppercase">Organization name</div>
            <input
              type="text" value={orgName} onChange={(e) => setOrgName(e.target.value)}
              placeholder="Acme Corp"
              data-testid="enterprise-org-name"
              className="mt-1 w-full font-display font-black text-2xl border-b-2 border-zinc-950 bg-transparent outline-none focus:border-[#FF3E00]"
            />
          </div>
          <div>
            <div className="font-mono text-[10px] tracking-[0.18em] text-zinc-500 uppercase">Owner email</div>
            <input
              type="email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)}
              placeholder="cto@acme.com"
              data-testid="enterprise-owner-email"
              className="mt-1 w-full font-mono text-sm border border-zinc-300 px-3 py-2 outline-none focus:border-zinc-950"
            />
            <div className="text-[11px] text-zinc-500 mt-1">A 30-day activation invite will be emailed here immediately.</div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="font-mono text-[10px] tracking-[0.18em] text-zinc-500 uppercase">Seats</div>
              <input
                type="number" min="1" max="10000" value={seats}
                onChange={(e) => setSeats(parseInt(e.target.value, 10) || 1)}
                data-testid="enterprise-seats"
                className="mt-1 w-full font-display font-black text-2xl tnum border-b-2 border-zinc-950 bg-transparent outline-none focus:border-[#FF3E00]"
              />
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-[0.18em] text-zinc-500 uppercase">Trial days</div>
              <input
                type="number" min="0" max="365" value={trialDays}
                onChange={(e) => setTrialDays(parseInt(e.target.value, 10) || 0)}
                data-testid="enterprise-trial-days"
                className="mt-1 w-full font-display font-black text-2xl tnum border-b-2 border-zinc-950 bg-transparent outline-none focus:border-[#FF3E00]"
              />
              <div className="text-[10px] text-zinc-500 mt-0.5">0 = start paid immediately</div>
            </div>
          </div>
          <div className="border-t border-zinc-200 pt-4 flex gap-2">
            <button
              onClick={submit} disabled={busy}
              data-testid="enterprise-submit"
              className="flex-1 bg-[#FF3E00] text-white font-mono text-[11px] tracking-[0.15em] uppercase py-3 hover:bg-zinc-950 disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2">
              <Plus size={12} /> {busy ? "PROVISIONING…" : `SPIN UP ${seats}-SEAT TENANT`}
            </button>
            <button onClick={onClose} className="font-mono text-[10px] tracking-[0.12em] uppercase border border-zinc-300 px-4 hover:bg-zinc-100">
              CANCEL
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const TONE_CLASS = { warn: "text-[#FF3E00]", ok: "text-emerald-700" };

function StatCell({ label, value, tone }) {
  const toneClass = TONE_CLASS[tone] || "text-zinc-950";
  return (
    <div className="border-r border-zinc-200 last:border-r-0 px-5 py-4">
      <div className="font-mono text-[10px] tracking-[0.18em] text-zinc-500 uppercase">{label}</div>
      <div className={`font-display font-black text-3xl tnum mt-1.5 ${toneClass}`}>{value}</div>
    </div>
  );
}

function TrialCell({ t, isTrialing, trialTone }) {
  if (isTrialing && t.trial_days_left != null) {
    return (
      <>
        <div className={`font-bold ${trialTone}`}>{t.trial_days_left}d left</div>
        <div className="text-[10px] text-zinc-500 tnum">ends {fmtDate(t.trial_ends_at)}</div>
      </>
    );
  }
  if (t.trial_ends_at) {
    return <span className="text-zinc-400 text-[11px] font-mono">EXPIRED {fmtDate(t.trial_ends_at)}</span>;
  }
  return "—";
}

function TenantsTableView({ loading, tenants, selected, onToggleSel, onToggleSelAll, onExtend, onSetSeats, onEndDateTenant, onReactivateTenant, onDeleteUser }) {
  if (loading) return <div className="p-10 text-center font-mono text-xs text-zinc-400 pulse-dot">LOADING…</div>;
  if (tenants.length === 0) return <div className="p-10 text-center font-mono text-xs text-zinc-400">NO TENANTS FOUND</div>;
  const allSelected = tenants.length > 0 && tenants.every((t) => selected.has(t.tenant_id));
  return (
    <table className="w-full text-sm">
      <thead className="border-b border-zinc-200 bg-zinc-50 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
        <tr>
          <th className="w-10 text-center px-2 py-2.5">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={onToggleSelAll}
              className="accent-[#FF3E00]"
              data-testid="tenants-select-all"
              aria-label="Select all tenants"
            />
          </th>
          <th className="text-left px-4 py-2.5">Tenant</th>
          <th className="text-left px-4 py-2.5">Plan / Status</th>
          <th className="text-left px-4 py-2.5">Seats</th>
          <th className="text-left px-4 py-2.5">Trial</th>
          <th className="text-left px-4 py-2.5">Users</th>
          <th className="text-left px-4 py-2.5">Created</th>
          <th className="text-right px-4 py-2.5">Actions</th>
        </tr>
      </thead>
      <tbody>
        {tenants.map((t) => (
          <TenantRow
            key={t.tenant_id}
            t={t}
            selected={selected.has(t.tenant_id)}
            onToggleSel={() => onToggleSel(t.tenant_id)}
            onExtend={onExtend}
            onSetSeats={onSetSeats}
            onEndDateTenant={onEndDateTenant}
            onReactivateTenant={onReactivateTenant}
            onDeleteUser={onDeleteUser}
          />
        ))}
      </tbody>
    </table>
  );
}

function TenantRow({ t, selected, onToggleSel, onExtend, onSetSeats, onEndDateTenant, onReactivateTenant, onDeleteUser }) {
  const [expanded, setExpanded] = useState(false);
  const [users, setUsers] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const toggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && users === null) {
      setLoadingUsers(true);
      try {
        const { data } = await api.get(`/platform/tenants/${t.tenant_id}/users`);
        setUsers(data.users || []);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail, errContext(err)));
        setUsers([]);
      } finally { setLoadingUsers(false); }
    }
  };

  const isPlatform = t.is_platform;
  const isTrialing = t.status === "trialing";
  const trialTone = t.trial_days_left != null && t.trial_days_left <= 3 ? "text-[#D0021B]" : "text-amber-700";
  return (
    <>
    <tr className="border-b border-zinc-100 align-top hover:bg-zinc-50" data-testid={`tenant-row-${t.tenant_id}`}>
      <td className="text-center px-2 py-3">
        {!isPlatform && (
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSel}
            className="accent-[#FF3E00]"
            data-testid={`tenant-select-${t.tenant_id}`}
            aria-label={`Select tenant ${t.name}`}
          />
        )}
      </td>
      <td className="px-4 py-3">
        <button
          onClick={toggle}
          disabled={isPlatform}
          data-testid={`tenant-expand-${t.tenant_id}`}
          className="flex items-start gap-2 text-left group disabled:cursor-default">
          {!isPlatform && (
            expanded ? <CaretDown size={12} className="mt-1 text-zinc-500" />
                     : <CaretRight size={12} className="mt-1 text-zinc-400 group-hover:text-zinc-950" />
          )}
          <div>
            <div className="font-semibold text-sm group-hover:text-[#FF3E00]">{t.name || "—"}</div>
            <div className="font-mono text-[10px] text-zinc-400 mt-0.5">{t.tenant_id}</div>
            {t.owner_email && <div className="text-[11px] text-zinc-500 mt-1">{t.owner_email}</div>}
          </div>
        </button>
      </td>
      <td className="px-4 py-3">
        {isPlatform ? (
          <span className="font-mono text-[10px] text-purple-700 bg-purple-50 border border-purple-200 px-2 py-0.5">PLATFORM</span>
        ) : t.ended_at ? (
          <>
            <span className="font-mono text-[10px] text-white bg-[#D0021B] border border-[#D0021B] px-2 py-0.5">END-DATED</span>
            <div className="text-[10px] text-zinc-500 mt-1">{fmtDate(t.ended_at)}</div>
          </>
        ) : (
          <>
            <div className="font-mono text-[11px]">{t.plan || "—"}</div>
            <div className={`font-mono text-[10px] mt-0.5 ${isTrialing ? "text-amber-700" : "text-zinc-500"}`}>
              {t.status || "—"}
            </div>
          </>
        )}
      </td>
      <td className="px-4 py-3 tnum text-sm">
        {t.seats_purchased != null ? (
          <>
            <span className="font-bold">{t.seats_used ?? 0}</span>
            <span className="text-zinc-400"> / {t.seats_purchased}</span>
          </>
        ) : "—"}
      </td>
      <td className="px-4 py-3 text-sm">
        <TrialCell t={t} isTrialing={isTrialing} trialTone={trialTone} />
      </td>
      <td className="px-4 py-3 tnum text-sm">{t.user_count ?? 0}</td>
      <td className="px-4 py-3 text-[11px] text-zinc-500 tnum">{fmtDate(t.created_at)}</td>
      <td className="px-4 py-3 text-right">
        {!isPlatform && (
          <div className="flex flex-wrap gap-2 justify-end">
            {t.ended_at ? (
              <button
                onClick={() => onReactivateTenant(t)}
                data-testid={`reactivate-tenant-${t.tenant_id}`}
                className="font-mono text-[10px] tracking-[0.12em] uppercase border border-emerald-600 text-emerald-700 px-2.5 py-1.5 hover:bg-emerald-600 hover:text-white transition-colors">
                REACTIVATE
              </button>
            ) : (
              <>
                <button
                  onClick={() => onExtend(t)}
                  data-testid={`extend-trial-${t.tenant_id}`}
                  className="font-mono text-[10px] tracking-[0.12em] uppercase border border-zinc-950 px-2.5 py-1.5 hover:bg-zinc-950 hover:text-white transition-colors">
                  +TRIAL
                </button>
                <button
                  onClick={() => onSetSeats(t)}
                  data-testid={`set-seats-${t.tenant_id}`}
                  className="font-mono text-[10px] tracking-[0.12em] uppercase border border-zinc-300 px-2.5 py-1.5 hover:bg-zinc-100 transition-colors">
                  SEATS
                </button>
                <button
                  onClick={() => onEndDateTenant(t)}
                  data-testid={`end-date-tenant-${t.tenant_id}`}
                  title="Close this tenant. Blocks logins until reactivated. Data is preserved."
                  className="font-mono text-[10px] tracking-[0.12em] uppercase border border-[#D0021B] text-[#D0021B] px-2.5 py-1.5 hover:bg-[#D0021B] hover:text-white transition-colors">
                  END-DATE
                </button>
              </>
            )}
          </div>
        )}
      </td>
    </tr>
    {expanded && !isPlatform && (
      <tr data-testid={`tenant-users-${t.tenant_id}`} className="bg-zinc-50 border-b border-zinc-100">
        <td colSpan={8} className="px-6 py-3">
          <div className="font-mono text-[10px] tracking-[0.15em] text-zinc-500 uppercase mb-2">
            Users in this tenant {users && `(${users.length})`}
          </div>
          {loadingUsers ? (
            <div className="font-mono text-[10px] text-zinc-400 pulse-dot">LOADING USERS…</div>
          ) : users && users.length > 0 ? (
            <table className="w-full text-[12px]">
              <thead className="font-mono text-[9px] uppercase tracking-[0.12em] text-zinc-400">
                <tr>
                  <th className="text-left py-1">Name / Email</th>
                  <th className="text-left py-1">Role</th>
                  <th className="text-left py-1">Modules</th>
                  <th className="text-left py-1">Joined</th>
                  <th className="text-left py-1">MFA</th>
                  <th className="text-right py-1 pr-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-zinc-200">
                    <td className="py-1.5">
                      <div className="font-semibold">{u.name || "—"}</div>
                      <div className="text-[10px] text-zinc-500 font-mono">{u.email}</div>
                    </td>
                    <td className="py-1.5">
                      <span className="font-mono text-[10px] bg-white border border-zinc-300 px-1.5 py-0.5">
                        {u.custom_role_name || u.role?.toUpperCase() || "USER"}
                      </span>
                    </td>
                    <td className="py-1.5 text-[10px] text-zinc-600 font-mono">
                      {(u.effective_modules || u.module_access || []).join(" · ") || <span className="text-zinc-400">—</span>}
                    </td>
                    <td className="py-1.5 text-[10px] text-zinc-500 tnum">{fmtDate(u.created_at)}</td>
                    <td className="py-1.5">
                      <span className={`font-mono text-[9px] ${u.mfa_enabled ? "text-emerald-700" : "text-zinc-400"}`}>
                        {u.mfa_enabled ? "ON" : "OFF"}
                      </span>
                    </td>
                    <td className="py-1.5 text-right pr-2">
                      {u.role === "superuser" ? (
                        <span className="font-mono text-[9px] text-zinc-400">PROTECTED</span>
                      ) : (
                        <button
                          onClick={async () => {
                            const ok = await onDeleteUser(t, u);
                            if (ok) setUsers((prev) => (prev || []).filter((x) => x.id !== u.id));
                          }}
                          data-testid={`delete-user-${u.id}`}
                          className="font-mono text-[9px] tracking-[0.1em] uppercase text-[#D0021B] hover:text-white hover:bg-[#D0021B] border border-[#D0021B] px-2 py-0.5 transition-colors"
                        >
                          DELETE
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="font-mono text-[10px] text-zinc-400">NO USERS</div>
          )}
        </td>
      </tr>
    )}
    </>
  );
}

function AccessRequestsPanel({ requests, onDecision }) {
  const [rejecting, setRejecting] = useState(null); // { id, reason }
  const [busy, setBusy] = useState("");
  const [selected, setSelected] = useState(new Set());

  // Drop selections that reference requests that no longer exist (after each refresh)
  useEffect(() => {
    setSelected((prev) => {
      const validIds = new Set(requests.map((r) => r.id));
      const next = new Set([...prev].filter((id) => validIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [requests]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => (prev.size === requests.length ? new Set() : new Set(requests.map((r) => r.id))));
  };

  const bulkApprove = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (!window.confirm(`Approve ${ids.length} request${ids.length === 1 ? "" : "s"}? Each requester will receive their own activation link.`)) return;
    setBusy("bulk");
    try {
      const { data } = await api.post("/platform/access-requests/bulk-approve", ids);
      toast.success(`Approved ${data.approved_count} · Skipped ${data.skipped_count}`, { duration: 8000 });
      setSelected(new Set());
      onDecision();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, errContext(err)));
    } finally { setBusy(""); }
  };

  const purgeRejected = async () => {
    if (!window.confirm("Permanently delete all rejected access requests?")) return;
    setBusy("purge");
    try {
      const { data } = await api.post("/platform/access-requests/purge?status=rejected");
      toast.success(`Purged ${data.deleted} rejected request${data.deleted === 1 ? "" : "s"}`);
      onDecision();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, errContext(err)));
    } finally { setBusy(""); }
  };

  const approve = async (r) => {
    setBusy(r.id);
    try {
      const { data } = await api.post(`/platform/access-requests/${r.id}/approve`);
      toast.success(`Approved ${r.email} — activation link sent.`, {
        action: {
          label: "COPY LINK",
          onClick: () => {
            navigator.clipboard?.writeText(data.activation_link);
            toast.success("Activation link copied");
          },
        },
        duration: 10000,
      });
      onDecision();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, errContext(err)));
    } finally { setBusy(""); }
  };

  const submitReject = async () => {
    if (!rejecting) return;
    setBusy(rejecting.id);
    try {
      await api.post(`/platform/access-requests/${rejecting.id}/reject`, { reason: rejecting.reason || undefined });
      toast.success("Request declined — requester notified.");
      setRejecting(null);
      onDecision();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, errContext(err)));
    } finally { setBusy(""); }
  };

  if (!requests || requests.length === 0) return null;

  return (
    <div className="mt-6 border-2 border-[#FF3E00]" data-testid="access-requests-panel">
      <div className="bg-[#FF3E00] text-white px-4 py-2 flex items-center gap-2">
        <EnvelopeSimple size={14} weight="fill" />
        <span className="font-mono text-[11px] tracking-[0.2em] uppercase">/ Pending access requests</span>
        <span className="font-mono text-[10px] bg-white/20 px-1.5 py-0.5">{requests.length}</span>
        {selected.size > 0 && (
          <button
            onClick={bulkApprove}
            disabled={busy === "bulk"}
            data-testid="bulk-approve-btn"
            className="font-mono text-[10px] tracking-[0.12em] uppercase bg-emerald-600 hover:bg-white hover:text-emerald-700 px-3 py-1 disabled:opacity-50 inline-flex items-center gap-1.5">
            <Check size={11} weight="bold" /> {busy === "bulk" ? "APPROVING…" : `APPROVE SELECTED (${selected.size})`}
          </button>
        )}
        <button
          onClick={purgeRejected}
          disabled={busy === "purge"}
          data-testid="purge-rejected-btn"
          title="Delete all rejected requests to free up the list"
          className="ml-auto font-mono text-[9px] tracking-[0.12em] uppercase bg-white/10 hover:bg-white/20 px-2 py-0.5 disabled:opacity-50">
          {busy === "purge" ? "PURGING…" : "PURGE REJECTED"}
        </button>
      </div>
      {requests.length > 1 && (
        <div className="px-4 py-1.5 bg-zinc-50 border-b border-zinc-200 flex items-center gap-2">
          <input
            type="checkbox"
            id="access-select-all"
            data-testid="access-select-all"
            checked={selected.size === requests.length && requests.length > 0}
            ref={(el) => { if (el) el.indeterminate = selected.size > 0 && selected.size < requests.length; }}
            onChange={toggleAll}
            className="w-3.5 h-3.5 accent-[#FF3E00]"
          />
          <label htmlFor="access-select-all" className="font-mono text-[10px] tracking-[0.15em] uppercase text-zinc-500 cursor-pointer">
            {selected.size === 0 ? "Select all" : `${selected.size} selected`}
          </label>
        </div>
      )}
      <ul className="divide-y divide-zinc-100">
        {requests.map((r) => (
          <li key={r.id} data-testid={`access-request-${r.id}`} className="px-4 py-3 flex items-start gap-3 hover:bg-zinc-50">
            <input
              type="checkbox"
              data-testid={`access-select-${r.id}`}
              checked={selected.has(r.id)}
              onChange={() => toggle(r.id)}
              disabled={rejecting?.id === r.id}
              className="mt-1 w-3.5 h-3.5 accent-[#FF3E00]"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="font-semibold text-sm">{r.name}</span>
                <span className="text-[11px] text-zinc-500 font-mono">{r.email}</span>
              </div>
              <div className="text-[11px] text-zinc-500 mt-0.5">
                <strong>{r.org_name}</strong> · requested {fmtDate(r.requested_at)}
              </div>
              {r.reason && (
                <div className="mt-1.5 text-[12px] text-zinc-700 border-l-2 border-zinc-300 pl-2 italic">&ldquo;{r.reason}&rdquo;</div>
              )}
              {rejecting?.id === r.id && (
                <div className="mt-2 flex gap-2 items-start">
                  <textarea
                    autoFocus
                    value={rejecting.reason}
                    onChange={(e) => setRejecting({ ...rejecting, reason: e.target.value })}
                    placeholder="Reason (will be emailed to requester)"
                    data-testid={`reject-reason-${r.id}`}
                    className="flex-1 text-[12px] border border-zinc-300 px-2 py-1.5 outline-none focus:border-zinc-950 resize-none"
                    rows={2}
                  />
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={submitReject}
                      disabled={busy === r.id}
                      data-testid={`reject-confirm-${r.id}`}
                      className="font-mono text-[10px] tracking-[0.12em] uppercase bg-[#D0021B] text-white px-3 py-1.5 hover:bg-zinc-950 disabled:opacity-50">
                      CONFIRM
                    </button>
                    <button
                      onClick={() => setRejecting(null)}
                      className="font-mono text-[10px] tracking-[0.12em] uppercase border border-zinc-300 px-3 py-1.5 hover:bg-zinc-100">
                      CANCEL
                    </button>
                  </div>
                </div>
              )}
            </div>
            {rejecting?.id !== r.id && (
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => approve(r)}
                  disabled={busy === r.id}
                  data-testid={`approve-request-${r.id}`}
                  className="font-mono text-[10px] tracking-[0.12em] uppercase bg-emerald-600 text-white px-3 py-1.5 hover:bg-zinc-950 disabled:opacity-50 inline-flex items-center gap-1">
                  <Check size={11} weight="bold" /> APPROVE
                </button>
                <button
                  onClick={() => setRejecting({ id: r.id, reason: "" })}
                  disabled={busy === r.id}
                  data-testid={`reject-request-${r.id}`}
                  className="font-mono text-[10px] tracking-[0.12em] uppercase border border-zinc-300 px-3 py-1.5 hover:bg-red-50 hover:border-[#D0021B] hover:text-[#D0021B] inline-flex items-center gap-1">
                  <XCircle size={11} /> REJECT
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function PresetAnalyticsCard({ data }) {
  if (!data) return null;
  const maxAdoptions = Math.max(1, ...data.presets.map((p) => p.adoptions));
  const labels = {
    accountant: "Accountant",
    hr_manager: "HR Manager",
    ops_lead: "Ops Lead",
    auditor: "Auditor",
  };
  return (
    <div className="mt-6 border border-zinc-950" data-testid="preset-analytics-card">
      <div className="border-b border-zinc-950 bg-zinc-50 px-4 py-2 flex items-center gap-2">
        <Sparkle size={14} weight="fill" className="text-[#FF3E00]" />
        <span className="font-mono text-[11px] tracking-[0.18em] uppercase">/ Preset adoption</span>
        <span className="text-[10px] text-zinc-500">
          ({data.total_adoptions} adoption{data.total_adoptions === 1 ? "" : "s"} across {data.active_tenants} tenants)
        </span>
      </div>
      <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {data.presets.map((p) => {
          const barPct = (p.adoptions / maxAdoptions) * 100;
          const lastAdopted = p.last_adopted_at
            ? new Date(p.last_adopted_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })
            : "never";
          return (
            <div
              key={p.preset_key}
              data-testid={`preset-analytics-${p.preset_key}`}
              className="border border-zinc-200 p-3 hover:border-zinc-950 transition-colors"
            >
              <div className="font-mono text-[10px] tracking-[0.15em] uppercase text-zinc-500">
                {labels[p.preset_key] || p.preset_key}
              </div>
              <div className="font-display font-black text-3xl tnum mt-1">{p.adoptions}</div>
              <div className="text-[11px] text-zinc-500">
                {p.unique_tenants} tenant{p.unique_tenants === 1 ? "" : "s"} · {p.pct_of_tenants}%
              </div>
              <div className="mt-2 h-1 bg-zinc-100 relative">
                <div className="absolute inset-y-0 left-0 bg-[#FF3E00] transition-all"
                  style={{ width: `${barPct}%` }} />
              </div>
              <div className="text-[10px] text-zinc-400 mt-1.5 font-mono">last: {lastAdopted}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function PlatformPage() {
  const { user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [presetStats, setPresetStats] = useState(null);
  const [accessRequests, setAccessRequests] = useState([]);
  const [fawtara, setFawtara] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [loading, setLoading] = useState(true);
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  // Multi-select for bulk tenant end-date / hard-delete. Set of tenant_id.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ts, ms, ps, ar, fw] = await Promise.all([
        api.get(`/platform/tenants?limit=500&status_filter=${statusFilter}${q ? `&q=${encodeURIComponent(q)}` : ""}`),
        api.get("/platform/metrics"),
        api.get("/platform/preset-analytics").catch(() => ({ data: null })),
        api.get("/platform/access-requests?status=pending").catch(() => ({ data: { items: [] } })),
        api.get("/platform/fawtara-status").catch(() => ({ data: null })),
      ]);
      setTenants(ts.data.items || []);
      setMetrics(ms.data);
      setPresetStats(ps.data);
      setAccessRequests(ar.data?.items || []);
      setFawtara(fw.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, errContext(err)));
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const onExtend = async (t) => {
    const raw = window.prompt(`Extend trial for "${t.name}" by how many days?`, "14");
    const days = parseInt(raw, 10);
    if (!days || days < 1) return;
    try {
      const { data } = await api.post(`/platform/tenants/${t.tenant_id}/extend-trial`, { days });
      toast.success(`Trial extended to ${fmtDate(data.trial_ends_at)}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, errContext(err)));
    }
  };

  const onSetSeats = async (t) => {
    const raw = window.prompt(`Set seat cap for "${t.name}" (current: ${t.seats_purchased ?? "—"})`, String(t.seats_purchased ?? 5));
    const seats = parseInt(raw, 10);
    if (!seats || seats < 1) return;
    try {
      await api.post(`/platform/tenants/${t.tenant_id}/seats`, { seats_purchased: seats });
      toast.success(`Seat cap set to ${seats}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, errContext(err)));
    }
  };

  const onEndDateTenant = async (t) => {
    if (!window.confirm(`End-date tenant "${t.name}"?\n\nAll users of this tenant will be blocked from logging in. Their data is preserved and can be reactivated later. This does not delete anything.`)) return;
    try {
      await api.post(`/platform/tenants/${t.tenant_id}/end-date`);
      toast.success(`${t.name} has been end-dated`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, errContext(err)));
    }
  };

  const onReactivateTenant = async (t) => {
    try {
      await api.post(`/platform/tenants/${t.tenant_id}/reactivate`);
      toast.success(`${t.name} reactivated`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, errContext(err)));
    }
  };

  const onDeleteUser = async (t, u) => {
    if (!window.confirm(`Delete user "${u.email}" from "${t.name}" permanently?\n\nThe user account will be removed and they will lose access immediately. Audit history is preserved.`)) return false;
    try {
      await api.delete(`/platform/tenants/${t.tenant_id}/users/${u.id}`);
      toast.success(`Removed ${u.email}`);
      load();
      return true;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, errContext(err)));
      return false;
    }
  };

  // ---- Bulk tenant actions ------------------------------------------------
  const toggleSel = (tid) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(tid)) n.delete(tid); else n.add(tid);
      return n;
    });
  };
  const toggleSelAll = () => {
    setSelected((s) => (s.size === tenants.length ? new Set() : new Set(tenants.map((t) => t.tenant_id))));
  };
  const clearSel = () => setSelected(new Set());

  const selectedNames = () => tenants
    .filter((t) => selected.has(t.tenant_id))
    .map((t) => `• ${t.name} (${t.tenant_id})`).join("\n");

  const onBulkEndDate = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`End-date ${selected.size} tenant(s)?\n\n${selectedNames()}\n\nUsers of these tenants will be blocked from logging in. Data is preserved and can be reactivated later — nothing is deleted yet.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/platform/tenants/bulk-end-date", { tenant_ids: Array.from(selected) });
      const skipped = (data.skipped || []).length;
      const endedIds = data.ended || [];
      clearSel();
      load();
      // 30-second UNDO — one click reactivates every tenant we just closed.
      // Sonner keeps the toast on-screen until the duration expires or the
      // user hits UNDO. Skipped rows are surfaced as an inline description.
      if (endedIds.length > 0) {
        toast.success(`${data.ended_count} tenant(s) end-dated`, {
          duration: 30000,
          description: skipped ? `${skipped} skipped (platform / already ended)` : "Click UNDO within 30 seconds to reactivate.",
          action: {
            label: "UNDO",
            onClick: async () => {
              try {
                const { data: undoRes } = await api.post("/platform/tenants/bulk-reactivate", { tenant_ids: endedIds });
                toast.success(`Reactivated ${undoRes.reactivated_count} tenant(s)`);
                load();
              } catch (undoErr) {
                toast.error(formatApiError(undoErr.response?.data?.detail, errContext(undoErr)) || "Undo failed");
              }
            },
          },
        });
      } else {
        toast.info(`No tenants end-dated${skipped ? ` (${skipped} skipped)` : ""}`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, errContext(err)));
    } finally {
      setBulkBusy(false);
    }
  };

  const onBulkDelete = async () => {
    if (selected.size === 0) return;
    // Two guards: informational confirm, then a typed "DELETE" prompt.
    if (!window.confirm(`HARD DELETE ${selected.size} tenant(s)?\n\n${selectedNames()}\n\nThis is IRREVERSIBLE and removes every user, employee, invoice, journal, leave, e-invoice and audit row belonging to these tenants.\n\nEach tenant must already be end-dated first — the backend will refuse otherwise. Continue?`)) return;
    const typed = window.prompt(`To authorize hard delete, type DELETE (all caps) exactly:`);
    if (typed !== "DELETE") {
      toast.error("Cancelled — you must type DELETE exactly to authorize.");
      return;
    }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/platform/tenants/bulk-delete", {
        tenant_ids: Array.from(selected), confirm_text: "DELETE",
      });
      const totalDocs = (data.deleted || []).reduce((sum, d) => sum + (d.total_docs_removed || 0), 0);
      const skipped = (data.skipped || []).length;
      toast.success(`${data.deleted_count} tenant(s) hard-deleted · ${totalDocs} docs removed${skipped ? ` · ${skipped} skipped` : ""}`, { duration: 10000 });
      if (skipped) {
        if (process.env.NODE_ENV !== "production") console.warn("[BULK-DELETE] Skipped:", data.skipped);
      }
      clearSel();
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, errContext(err)));
    } finally {
      setBulkBusy(false);
    }
  };

  if (user && user.role !== "superuser") return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-white" data-testid="platform-page">
      {/* Header */}
      <header className="border-b border-zinc-950 bg-zinc-950 text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CrownSimple size={20} weight="fill" className="text-[#FF3E00]" />
            <div>
              <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-400">SMARTERP / PLATFORM CONSOLE</div>
              <div className="font-display font-black text-lg tracking-tight">Owner control room</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <TapModeBadge mode={metrics?.payment_mode || metrics?.stripe_mode} />
            <FawtaraModeBadge status={fawtara} />
            <button
              onClick={() => setShowBulkModal(true)}
              data-testid="open-bulk-users-btn"
              className="font-mono text-[10px] tracking-[0.15em] uppercase border border-zinc-700 px-3 py-1.5 hover:bg-white hover:text-zinc-950 transition-colors inline-flex items-center gap-2">
              <Users size={12} /> BULK USERS
            </button>
            <button
              onClick={() => setShowEnterpriseModal(true)}
              data-testid="new-enterprise-btn"
              className="font-mono text-[10px] tracking-[0.15em] uppercase bg-[#FF3E00] text-white px-3 py-1.5 hover:bg-white hover:text-zinc-950 transition-colors inline-flex items-center gap-2">
              <Plus size={12} weight="bold" /> NEW ENTERPRISE
            </button>
            <button
              onClick={load}
              data-testid="platform-refresh"
              className="font-mono text-[10px] tracking-[0.15em] uppercase border border-zinc-700 px-3 py-1.5 hover:bg-white hover:text-zinc-950 transition-colors inline-flex items-center gap-2">
              <ArrowsClockwise size={12} /> REFRESH
            </button>
            <ResetDemoButton onDone={load} />
            <a href="/" className="font-mono text-[10px] tracking-[0.15em] uppercase text-zinc-400 hover:text-white">← EXIT</a>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6">
        {/* Metrics */}
        {metrics && (
          <div className="border border-zinc-950 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6" data-testid="platform-metrics">
            <StatCell label="Tenants" value={metrics.tenants_total} />
            <StatCell label="Trialing" value={metrics.trialing} tone="warn" />
            <StatCell label="Paid" value={metrics.active_paid} tone="ok" />
            <StatCell label="Expiring ≤3d" value={metrics.trials_expiring_in_3d} tone={metrics.trials_expiring_in_3d > 0 ? "warn" : ""} />
            <StatCell label="Users" value={metrics.users_total} />
            <StatCell label="Seats used" value={`${metrics.seats_used_total}/${metrics.seats_purchased_total}`} />
          </div>
        )}

        <RevenueWidget />

        <AccessRequestsPanel requests={accessRequests} onDecision={load} />

        <PresetAnalyticsCard data={presetStats} />

        <DocumentationSection />

        {/* Search + Table */}
        <div className="mt-6 border border-zinc-950">
          <div className="border-b border-zinc-950 bg-zinc-50 px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <Buildings size={14} />
              <span className="font-mono text-[11px] tracking-[0.18em] uppercase">/ TENANTS</span>
              <span className="text-[10px] text-zinc-500">({tenants.length})</span>
              <div className="flex items-center gap-1 ml-3">
                {[
                  { key: "active", label: "ACTIVE" },
                  { key: "closed", label: "CLOSED" },
                  { key: "all", label: "ALL" },
                ].map((p) => (
                  <button
                    key={p.key}
                    onClick={() => setStatusFilter(p.key)}
                    data-testid={`tenant-filter-${p.key}`}
                    className={`font-mono text-[10px] tracking-[0.12em] uppercase px-2.5 py-1 border transition-colors ${
                      statusFilter === p.key
                        ? "bg-zinc-950 text-white border-zinc-950"
                        : "bg-white text-zinc-500 border-zinc-300 hover:border-zinc-950 hover:text-zinc-950"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <input
              data-testid="platform-search"
              type="text"
              placeholder="search name / owner email…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="font-mono text-xs border border-zinc-300 px-3 py-1.5 w-64 outline-none focus:border-zinc-950 bg-white"
            />
          </div>

          <div className="overflow-x-auto">
            {selected.size > 0 && (
              <div className="border-b border-zinc-950 bg-amber-50 px-4 py-2 flex items-center gap-3 text-xs flex-wrap" data-testid="tenants-bulk-bar">
                <span className="font-mono tracking-wider">{selected.size} SELECTED</span>
                <button
                  onClick={onBulkEndDate}
                  disabled={bulkBusy}
                  data-testid="tenants-bulk-end-date-btn"
                  className="font-mono text-[10px] tracking-[0.15em] uppercase border border-zinc-950 px-3 py-1 hover:bg-zinc-950 hover:text-white disabled:opacity-40"
                >End-date selected</button>
                <button
                  onClick={onBulkDelete}
                  disabled={bulkBusy}
                  data-testid="tenants-bulk-delete-btn"
                  title="Hard delete — permanently removes each tenant and every document it owns. Requires prior end-date."
                  className="font-mono text-[10px] tracking-[0.15em] uppercase border border-[#D0021B] text-[#D0021B] px-3 py-1 hover:bg-[#D0021B] hover:text-white disabled:opacity-40"
                >Hard delete selected</button>
                <button
                  onClick={clearSel}
                  data-testid="tenants-bulk-clear-btn"
                  className="font-mono text-[10px] tracking-[0.15em] uppercase text-zinc-500 hover:text-zinc-950"
                >Clear</button>
              </div>
            )}
            <TenantsTableView
              loading={loading}
              tenants={tenants}
              selected={selected}
              onToggleSel={toggleSel}
              onToggleSelAll={toggleSelAll}
              onExtend={onExtend}
              onSetSeats={onSetSeats}
              onEndDateTenant={onEndDateTenant}
              onReactivateTenant={onReactivateTenant}
              onDeleteUser={onDeleteUser}
            />
          </div>
        </div>
      </main>
      <EnterpriseTemplateModal
        open={showEnterpriseModal}
        onClose={() => setShowEnterpriseModal(false)}
        onCreated={load}
      />
      <BulkUsersModal
        open={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        onDone={load}
      />
    </div>
  );
}
